import { useEffect, useState } from "react";
import "@/styles/Layout.scss";
import apiClient from "@/services";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
// Scripts
import { auth } from "@/utils/firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import getProfile from "@/scripts/getProfile";
import { getUser } from "@/hooks/useLocalStorage";
import updateProfile from "@/scripts/updateProfile";
import { COUNTRIES, NOTIFICATION_OPTIONS } from "@/hooks/useConstant";
import ResetAlert from "@/components/ResetAlert";

const formSchema = z.object({
  firstName: z.string().min(2, { message: "First name is required" }),
  lastName: z.string().min(2, { message: "Last name is required" }),
  phoneNumber: z
    .string()
    .min(
      10,
      "Phone number must be at least 10 digits, including the country code"
    )
    .max(15, "Phone number cannot exceed 15 digits")
    .regex(
      /^\+[1-9]\d{9,14}$/,
      "Phone number must start with '+' followed by a valid country code and digits"
    ),
  companyName: z.string().min(2, { message: "Company name is required" }),
  website: z.string().url({ message: "Invalid website URL" }),
  agent: z.string().optional(),
  country: z.string().min(2, { message: "Please select a country" }),
  notificationPreference: z.string().optional(),
});

function Profile() {
  const user = getUser();
  const [email, setEmail] = useState();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      companyName: "",
      website: "",
      agent: "",
      country: "",
      notificationPreference: "",
    },
  });
  const { reset } = form;

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      await updateProfile(user.uid, values);
      alert("User data updated!");
    } catch (error) {
      console.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getData = async () => {
    try {
      setLoading(true);
      const data = await getProfile(user.uid);

      try {
        // Fetch Agents
        const { data } = await apiClient.get("/getAgents");
        setAgents(data);
      } catch (err) {
        setAgents([]);
        console.log("Error fetching agents", err);
      }

      // Set form data
      reset({
        firstName: data.first_name || "",
        lastName: data.last_name || "",
        email: data.email || "",
        phoneNumber: data.phone || "",
        companyName: data.company || "",
        website: data.website || "",
        agent: data.agent || "",
        country: data.country || "",
        notificationPreference: data.notification || "",
      });
      // Set email for password resets
      setEmail(data.email);
    } catch (error) {
      console.error("Error fetching user data:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = () => {
    sendPasswordResetEmail(auth, email)
      .then(() => {
        setShowAlert(true);
        console.log("Reset email sent");
      })
      .catch((err) => {
        console.log("Error sending reset email:", err.code, err.message);
      });
  };

  useEffect(() => {
    getData();
  }, []);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper">
      <div>
        <h1 className="text-sm font-bold text-green ml-0.5">Profile Details</h1>
        <div className="highlight" />
      </div>

      <Form {...form}>
        <form
          className="space-y-4 mt-1 p-3"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex justify-between mobileFormWrapper">
            {/* First Name */}
            <div className="w-[49%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name*</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Last Name */}
            <div className="w-[49%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name*</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mobileFormWrapper">
            {/* Email */}
            <div className="w-[49%] mobileInputWrapper">
              <FormField
                disabled
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email*</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Phone Number */}
            <div className="w-[49%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number*</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+1234567890" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mobileFormWrapper">
            {/* Company Name */}
            <div className="w-[49%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name*</FormLabel>
                    <FormControl>
                      <Input placeholder="JP Associates" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="w-[49%] mobileInputWrapper">
              {/* Website */}
              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website*</FormLabel>
                    <FormControl>
                      <Input placeholder="https://www.example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mobileFormWrapper">
            {/* Agent Dropdown */}
            {(user.role === "buyer" || user.role === "seller") && (
              <div className="w-[49%] mobileInputWrapper">
                <FormField
                  control={form.control}
                  name="agent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Agent</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl className="h-10">
                          <SelectTrigger>
                            <SelectValue placeholder="Select an agent" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {agents.map((agent, i) => (
                            <SelectItem key={i} value={agent.user_id}>
                              {agent.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <div className="w-[49%] mobileInputWrapper">
              {/* Country Dropdown */}
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country*</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl className="h-10">
                        <SelectTrigger>
                          <SelectValue placeholder="Select a country" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div>
            <h1 className="text-sm font-bold text-green ml-0.5">
              Notifications
            </h1>
            <div className="highlight !w-[88px]" />
          </div>

          <div className="flex justify-between mobileFormWrapper">
            {/* Notifications */}
            <div className="w-[49%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="notificationPreference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Enable notifications</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl className="h-10">
                        <SelectTrigger>
                          <SelectValue placeholder="Select notification preference" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {NOTIFICATION_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="text-right">
            <Button
              type="submit"
              className="w-[200px] bg-green mobileInputWrapper"
            >
              Save
            </Button>
          </div>
        </form>
      </Form>

      <hr className="m-1" />

      <div className="p-3">
        <h1 className="text-sm font-bold text-green ml-0.5">Settings</h1>
        <div className="highlight !w-[60px]" />

        <p className="text-sm my-3">Reset account password</p>
        <Button
          type="submit"
          onClick={resetPassword}
          className="w-[200px] mobileInputWrapper"
        >
          Reset password
        </Button>

        {/* Alert */}
        <ResetAlert
          email={email}
          showAlert={showAlert}
          setShowAlert={setShowAlert}
        />
      </div>
    </div>
  );
}

export default Profile;
